/**
 * THE VERIFY PROMPT — a focused, once-per-session card for a logged-in user who has not verified.
 *
 * 🚨 WHY IT EXISTS, with the number. Measured against prod 2026-08-15 across the 178 signups since
 * EMAIL_VERIFICATION_ENABLED went live: 178 verify emails SENT, 82 verified, 96 NEVER CLICKED. Those
 * 96 are NOT dead leads — 44% of them were on the site in the last 7 days, against 52% of the
 * verified group. They come back at nearly the same rate and then sit at zero access, because the +5
 * day trial grant is withheld behind the click. 94% of them hold a zero balance. They convert at
 * 1.0% against 8.5%.
 *
 * The only thing standing between those two columns is a link nobody clicked, so the fix belongs
 * where the user actually is (the site) rather than where they are not (the inbox).
 *
 * ⭐ WHAT THIS DOES THAT THE THIN BANNER COULD NOT. The banner said "Resend email" and nothing else.
 * If your problem is that the mail is in Promotions, or you typo'd your address at signup, or the
 * 24h token already expired, "resend" alone cannot help you. So this shows the three facts that
 * actually let someone find it: the address we sent TO, the address it comes FROM, and the exact
 * subject line — all pulled from the server, never retyped, so they cannot drift into being wrong.
 *
 * ⚠ NOT A HARD WALL, ON PURPOSE. It defers for the session rather than blocking. These users are
 * already at zero premium access; locking the free surfaces too would push away the one audience
 * that still shows up. The persistent <VerifyEmailBanner/> keeps the thread after this is deferred.
 */
'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { Mail, MailCheck, Search, RefreshCw, Check, AlertCircle } from 'lucide-react'

/** Deferred for THIS TAB SESSION only. Not localStorage: a permanent dismissal is how the old banner
 *  went quiet for the 96 users this component exists for. */
const DEFER_KEY = 'dt_verify_prompt_deferred'

/** Fired by <VerifyEmailBanner/> to re-open this card after it was deferred. The banner is a one-line
 *  reminder; every actual instruction lives here, so its CTA reopens this rather than duplicating a
 *  half-answer that could drift out of sync. */
export const SHOW_VERIFY_PROMPT_EVENT = 'dt:show-verify-prompt'

/** `fromAddress` and `subject` come from the SERVER (verification-status), not from constants imported
 *  here: verification-core pulls in node `crypto` and must never reach a client bundle. Same single
 *  source of truth, so the instructions cannot drift away from the mail we actually send. */
type Status = {
  needsVerification: boolean
  email?: string
  fromAddress?: string
  subject?: string
  sentAt?: string | null
}
type SendState = { kind: 'idle' | 'sending' | 'sent' | 'error'; text?: string }

export default function VerifyEmailPrompt() {
  const { status } = useSession()
  const [info, setInfo] = useState<Status | null>(null)
  const [deferred, setDeferred] = useState(true) // assume deferred until we have read sessionStorage
  const [send, setSend] = useState<SendState>({ kind: 'idle' })

  useEffect(() => {
    try { setDeferred(sessionStorage.getItem(DEFER_KEY) === '1') } catch { setDeferred(false) }
  }, [])

  // Re-open on request from the persistent banner.
  useEffect(() => {
    const reopen = () => {
      try { sessionStorage.removeItem(DEFER_KEY) } catch { /* private mode */ }
      setSend({ kind: 'idle' })
      setDeferred(false)
    }
    window.addEventListener(SHOW_VERIFY_PROMPT_EVENT, reopen)
    return () => window.removeEventListener(SHOW_VERIFY_PROMPT_EVENT, reopen)
  }, [])

  useEffect(() => {
    if (status !== 'authenticated') return
    let alive = true
    fetch('/api/auth/verification-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setInfo(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [status])

  const defer = useCallback(() => {
    try { sessionStorage.setItem(DEFER_KEY, '1') } catch { /* private mode: just hide for now */ }
    setDeferred(true)
  }, [])

  const resend = useCallback(async () => {
    setSend({ kind: 'sending' })
    try {
      const r = await fetch('/api/auth/resend-verification', { method: 'POST' })
      const j = await r.json().catch(() => ({}))
      if (r.status === 429) {
        setSend({ kind: 'error', text: `Just sent one. Try again in ${j?.retryAfterSeconds ?? 60} seconds.` })
      } else if (j?.alreadyVerified) {
        setSend({ kind: 'sent', text: 'Already verified. You are all set.' })
        setInfo((p) => (p ? { ...p, needsVerification: false } : p))
      } else if (j?.ok && j?.sent) {
        setSend({ kind: 'sent', text: 'Sent. It usually arrives within a minute.' })
      } else {
        setSend({ kind: 'error', text: 'We could not send it just now. Try again in a moment.' })
      }
    } catch {
      setSend({ kind: 'error', text: 'Network problem. Check your connection and try again.' })
    }
  }, [])

  if (status !== 'authenticated' || deferred || !info?.needsVerification) return null

  const from = info.fromAddress || 'support@thedesperatetrader.com'
  const subject = info.subject || 'Verify your email for TheDesperateTrader'

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-prompt-title"
        className="w-full max-w-md overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900 shadow-2xl shadow-zinc-500/20 dark:shadow-black/60"
      >
        <div className="px-6 pt-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10">
            <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 id="verify-prompt-title" className="mt-4 text-lg font-bold text-zinc-900 dark:text-white">
            Confirm your email to unlock 5 more premium days
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            You are on your welcome day of full access right now. Confirming your email adds 5 more, with no card
            and nothing to pay.
          </p>
        </div>

        {/* The three facts that let someone actually FIND the email. All server-supplied. */}
        <div className="mx-6 mt-4 rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/70 p-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
            <Search className="h-3.5 w-3.5" />
            What to look for
          </div>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="w-14 shrink-0 text-zinc-500">Sent to</dt>
              <dd className="min-w-0 break-all font-medium text-zinc-700 dark:text-zinc-200">{info.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-14 shrink-0 text-zinc-500">From</dt>
              <dd className="min-w-0 break-all font-medium text-zinc-700 dark:text-zinc-200">{from}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-14 shrink-0 text-zinc-500">Subject</dt>
              <dd className="min-w-0 font-medium text-zinc-700 dark:text-zinc-200">{subject}</dd>
            </div>
          </dl>
          <p className="mt-3 border-t border-zinc-200 dark:border-zinc-800 pt-3 text-xs leading-relaxed text-zinc-500">
            Not in your inbox? Check <span className="font-semibold text-zinc-600 dark:text-zinc-400">Spam</span> and, in Gmail, the{' '}
            <span className="font-semibold text-zinc-600 dark:text-zinc-400">Promotions</span> tab. Searching for{' '}
            <span className="font-semibold text-zinc-600 dark:text-zinc-400">{from}</span> finds it fastest.
          </p>
        </div>

        {send.kind !== 'idle' && send.text && (
          <div
            className={`mx-6 mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
              send.kind === 'error'
                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
                : send.kind === 'sent'
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'
            }`}
          >
            {send.kind === 'error' ? <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" /> : <Check className="mt-px h-3.5 w-3.5 shrink-0" />}
            <span>{send.text}</span>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 px-6 py-4">
          <button
            type="button"
            onClick={resend}
            disabled={send.kind === 'sending'}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-bold text-zinc-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {send.kind === 'sending'
              ? <><RefreshCw className="h-4 w-4 animate-spin" />Sending</>
              : <><MailCheck className="h-4 w-4" />Send it again</>}
          </button>
          <button
            type="button"
            onClick={defer}
            className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-zinc-600 dark:text-zinc-400 transition-colors hover:text-zinc-900 dark:hover:text-zinc-200"
          >
            I will do this later
          </button>
          <p className="text-center text-[11px] leading-relaxed text-zinc-600">
            Wrong address, or still nothing after a resend? Email{' '}
            <a href={`mailto:${from}`} className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-300 underline">{from}</a> and we will
            sort it out by hand.
          </p>
        </div>
      </div>
    </div>
  )
}
